import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Loader2, Factory, Plus, Minus, Search } from "lucide-react";

function AdjustPopover({ label, onApply, testId }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const apply = async (sign) => {
    const n = Number(qty);
    if (!(n > 0)) { toast.error("Enter a quantity"); return; }
    await onApply(sign * n, note);
    setOpen(false); setQty(""); setNote("");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="rounded-sm border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-100" data-testid={testId}>Adjust</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-sm p-3" data-testid={`${testId}-panel`}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Adjust {label}</p>
        <Input inputMode="decimal" placeholder="Quantity" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 rounded-sm text-right tabular-nums" data-testid={`${testId}-qty`} />
        <Input placeholder="Note (e.g. opening balance, count fix)" value={note} onChange={(e) => setNote(e.target.value)} className="mt-2 h-8 rounded-sm" data-testid={`${testId}-note`} />
        <div className="mt-2 flex gap-2">
          <Button size="sm" variant="outline" className="h-8 flex-1 rounded-sm text-red-600" onClick={() => apply(-1)} data-testid={`${testId}-sub`}><Minus className="mr-1 h-3.5 w-3.5" /> Remove</Button>
          <Button size="sm" className="h-8 flex-1 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={() => apply(1)} data-testid={`${testId}-add`}><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function CoPackerInventory() {
  const [coPackers, setCoPackers] = useState([]);
  const [components, setComponents] = useState([]);
  const [stock, setStock] = useState([]);
  const [fg, setFg] = useState([]);
  const [cpId, setCpId] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [cps, comps, st, fgs] = await Promise.all([
        api.get("/co-packers"), api.get("/components"),
        api.get("/copacker-stock").catch(() => ({ data: [] })),
        api.get("/copacker-fg").catch(() => ({ data: [] })),
      ]);
      setCoPackers(cps.data || []);
      setComponents(comps.data || []);
      setStock(st.data || []);
      setFg(fgs.data || []);
      setCpId((cur) => cur || (cps.data?.[0]?.id ?? ""));
    } catch {
      toast.error("Failed to load co-packer inventory");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const stockMap = useMemo(() => {
    const m = {};
    for (const s of stock) if (s.co_packer_id === cpId) m[`${s.component_id}|${s.type_id || ""}|${s.design_id || ""}`] = s.qty || 0;
    return m;
  }, [stock, cpId]);
  const fgRows = useMemo(() => fg.filter((r) => r.co_packer_id === cpId), [fg, cpId]);

  const rows = useMemo(() => {
    const out = [];
    for (const c of components) {
      for (const t of c.types || []) {
        const designs = t.designs?.length ? t.designs : [null];
        for (const d of designs) {
          const key = `${c.id}|${t.id}|${d?.id || ""}`;
          out.push({ key, component: c, type: t, design: d, qty: stockMap[key] || 0,
            label: `${c.name} — ${t.name || t.label || ""}${d ? ` · ${d.name}` : ""}` });
        }
      }
    }
    const needle = q.trim().toLowerCase();
    return needle ? out.filter((r) => r.label.toLowerCase().includes(needle)) : out;
  }, [components, stockMap, q]);

  const adjustComponent = (r) => async (delta, note) => {
    try {
      await api.post("/copacker-stock/adjust", {
        co_packer_id: cpId, component_id: r.component.id, type_id: r.type.id, design_id: r.design?.id || "",
        label: r.label, delta, note,
      });
      toast.success("Balance adjusted");
      const { data } = await api.get("/copacker-stock");
      setStock(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to adjust"); }
  };

  const adjustFg = (r) => async (delta, note) => {
    try {
      await api.post("/copacker-fg/adjust", {
        co_packer_id: cpId, product_id: r.product_id, design_id: r.design_id || "", case_pack: r.case_pack || 0,
        product_name: r.product_name, design_name: r.design_name, delta, note,
      });
      toast.success("Balance adjusted");
      const { data } = await api.get("/copacker-fg");
      setFg(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to adjust"); }
  };

  const cp = coPackers.find((c) => c.id === cpId);
  const fmt = (n) => Math.round((n || 0) * 100) / 100;

  return (
    <main className="mx-auto max-w-[1100px] px-6 py-8 md:px-10 md:py-12" data-testid="copacker-inventory-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Operations</p>
      <h1 className="mt-1 font-heading text-4xl font-bold tracking-tighter text-slate-900 sm:text-5xl">Co-Packer Inventory</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-500">Components and finished goods held at each co-packer. Balances rise with shipments, fall with production and outbound deliveries — negatives flag discrepancies to reconcile.</p>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : coPackers.length === 0 ? (
        <div className="mt-10 border border-dashed border-slate-300 p-10 text-center">
          <Factory className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No co-packers yet — add one on the Contacts page first.</p>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Select value={cpId} onValueChange={setCpId}>
              <SelectTrigger className="w-64 rounded-sm" data-testid="cpi-copacker-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {coPackers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter components…" className="h-9 w-64 rounded-sm pl-8" data-testid="cpi-search" />
            </div>
          </div>

          <div className="mt-6 border border-slate-200" data-testid="cpi-fg-section">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-slate-700">Finished Goods at {cp?.company_name}</div>
            {fgRows.length === 0 ? (
              <p className="px-4 py-3 text-xs text-slate-400">Nothing here yet — finished goods land here when you record ALUM production against this co-packer.</p>
            ) : fgRows.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-4 py-2 text-sm odd:bg-white even:bg-slate-50/50" data-testid={`cpi-fg-${r.id}`}>
                <span className="text-slate-800">{r.product_name}{r.design_name ? ` · ${r.design_name}` : ""}{r.case_pack ? ` · ${Math.round(r.case_pack)}-Pack` : ""}</span>
                <span className="flex items-center gap-3">
                  <span className={`tabular-nums font-semibold ${r.cases < 0 ? "text-red-600" : "text-slate-800"}`} data-testid={`cpi-fg-qty-${r.id}`}>{fmt(r.cases).toLocaleString()} CS</span>
                  <AdjustPopover label="finished goods" onApply={adjustFg(r)} testId={`cpi-fg-adjust-${r.id}`} />
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 border border-slate-200" data-testid="cpi-component-section">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-slate-700">Components at {cp?.company_name}</div>
            {rows.map((r) => (
              <div key={r.key} className="flex items-center justify-between px-4 py-2 text-sm odd:bg-white even:bg-slate-50/50" data-testid={`cpi-row-${r.key}`}>
                <span className={r.qty ? "text-slate-800" : "text-slate-400"}>{r.label}</span>
                <span className="flex items-center gap-3">
                  <span className={`tabular-nums font-semibold ${r.qty < 0 ? "text-red-600" : r.qty ? "text-slate-800" : "text-slate-300"}`} data-testid={`cpi-qty-${r.key}`}>{fmt(r.qty).toLocaleString()}</span>
                  <AdjustPopover label={r.label} onApply={adjustComponent(r)} testId={`cpi-adjust-${r.key}`} />
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </main>
  );
}
